import { createSelector } from "@reduxjs/toolkit";
import isEqual from "lodash/isEqual";
import type { RootState } from "@/app/store";
import { selectCurrentConfiguration, selectHistoryChanges } from "@/features/configuration/selectors";
import { displayedShops, hasRealChanges, pendingShops } from "@/features/shops/lib";
import type { Shop } from "@/shared/types/shop";
import type { BillingInfo, LanguageOption, SelectedPlan, UserInfo } from "@/shared/types/user";
import { SUPPORTED_LOCALES } from "@/shared/i18n";

/**
 * Vista tipizzata sullo slice legacy. In fase 1 la verita' di questi campi resta li' perche' le viste
 * legacy li scrivono ancora; i selettori sono l'unico modo in cui lo shell nuovo li legge.
 * Sparisce quando i campi migrano negli slice nuovi (fasi 3-7).
 */
export interface LegacyShellState {
  websites: Shop[];
  userInfo: UserInfo;
  billingInfo: BillingInfo | null;
  selectedPlan: SelectedPlan;
  languages: LanguageOption[];
  newNotificationCounter: number;
  selectedCampaign: unknown;
  selectedCampaignSnapshot: unknown;
  selectedBanner: unknown;
  selectedBannerSnapshot: unknown;
  selectedRecommendation: unknown;
  selectedRecommendationSnapshot: unknown;
  showSaveConfigModal: boolean;
  pendingWebsite: Shop | null;
}

export {
  selectCurrentConfiguration,
  selectCurrentShopId,
  selectHistoryChanges,
} from "@/features/configuration/selectors";

const legacy = (state: RootState): LegacyShellState => state.legacy as unknown as LegacyShellState;

export const selectShops = (state: RootState): Shop[] => legacy(state).websites ?? [];
export const selectDisplayedShops = createSelector([selectShops], (shops) => displayedShops(shops));
export const selectPendingShops = createSelector([selectShops], (shops) => pendingShops(shops));
export const selectHasUnsavedConfiguration = createSelector(
  [selectDisplayedShops, selectCurrentConfiguration, selectHistoryChanges],
  (shops, configuration, history) => history.length > 0 && hasRealChanges(shops, configuration),
);
export const selectUser = (state: RootState): UserInfo => legacy(state).userInfo ?? {};
export const selectBillingInfo = (state: RootState): BillingInfo | null => legacy(state).billingInfo;
export const selectSelectedPlan = (state: RootState): SelectedPlan => legacy(state).selectedPlan ?? {};
export const selectUnreadNotifications = (state: RootState): number =>
  legacy(state).newNotificationCounter || 0;

const changed = (selected: unknown, snapshot: unknown): boolean =>
  Boolean(selected) && !isEqual(selected, snapshot);

export const selectEditorHasChanges = createSelector([legacy], (state) => {
  const campaign = changed(state.selectedCampaign, state.selectedCampaignSnapshot);
  const banner = changed(state.selectedBanner, state.selectedBannerSnapshot);
  const recommendation = changed(state.selectedRecommendation, state.selectedRecommendationSnapshot);
  return { campaign, banner, recommendation, any: campaign || banner || recommendation };
});

export const selectShowSaveConfigModal = (state: RootState): boolean =>
  Boolean(legacy(state).showSaveConfigModal);
export const selectPendingShop = (state: RootState): Shop | null => legacy(state).pendingWebsite ?? null;
export const selectSelectedEditors = createSelector([legacy], (state) => ({
  campaign: Boolean(state.selectedCampaign),
  banner: Boolean(state.selectedBanner),
}));

export const selectLayerVersionName = (state: RootState): string | undefined =>
  selectCurrentConfiguration(state)?.setup?.config?.layerVersion;
/** Versione del layer dello shop com'e' salvato (la lista `websites`), non quella in editing. */
export const selectSavedLayerVersionName = createSelector(
  [selectShops, selectCurrentConfiguration],
  // Per id e non con savedShopFor (url + lingua): mentre si modifica `language_iso` lo shop salvato
  // non si troverebbe e il picker mostrerebbe un avviso di versione cambiata spurio.
  (shops, configuration) =>
    shops.find((item) => String(item.id) === String(configuration?.id))?.setup?.config?.layerVersion,
);
export const selectShopIsActive = (state: RootState): boolean =>
  Boolean(selectCurrentConfiguration(state)?.is_active);
/** Primo valore del piano selezionato (`Object.values(getSelectedPlan)[0] || {}` nel legacy). */
export const selectPlanValue = createSelector(
  [selectSelectedPlan],
  (plan) => (Object.values(plan)[0] ?? {}) as { isActive?: boolean; key?: string },
);

const BACKOFFICE_LOCALES: readonly string[] = SUPPORTED_LOCALES; // Utils.LANGUAGE_OPTIONS
export const selectLanguages = (state: RootState): LanguageOption[] => legacy(state).languages ?? [];
/** getBOLanguages del legacy: solo le lingue dell'interfaccia (quelle con un JSON in src/locales). */
export const selectBackofficeLanguages = createSelector([selectLanguages], (languages) =>
  languages.filter((item) => BACKOFFICE_LOCALES.includes(item.value)),
);
